/**
 * DBS Portal Core Module Registry
 * 
 * Main entry point for the module registry system that provides:
 * - Automatic module discovery and registration
 * - Module metadata validation and management
 * - High-performance caching system
 * - Event-driven architecture
 * - TypeScript-first design
 * 
 * @example
 * ```typescript
 * import { ModuleRegistry } from '@dbs-portal/core-module-registry'
 * 
 * // Initialize registry
 * const registry = ModuleRegistry.getInstance()
 * await registry.initialize()
 * 
 * // Get all active modules
 * const modules = registry.getActiveModules()
 * 
 * // Register a new module
 * await registry.registerModule({
 *   id: 'my-module',
 *   name: 'My Module',
 *   version: '1.0.0',
 *   // ... other metadata
 * })
 * ```
 */

// Core Registry
export { ModuleRegistry } from './registry/ModuleRegistry'
export type { RegistryListener, RegistryOptions } from './registry/ModuleRegistry'

// Discovery Service (types only - class loaded dynamically)
export type { ModuleDiscoveryService } from './registry/ModuleDiscoveryService'
export type {
  DiscoveryOptions,
  DiscoveryResult,
  DiscoveryError,
  DiscoveryWarning,
  DiscoveryStats
} from './registry/ModuleDiscoveryService'

/**
 * Dynamically load ModuleDiscoveryService to avoid mixed import warnings
 */
export async function createModuleDiscoveryService(options?: import('./registry/ModuleDiscoveryService').DiscoveryOptions) {
  const { ModuleDiscoveryService } = await import(/* webpackChunkName: "discovery-service" */ './registry/ModuleDiscoveryService')
  return new ModuleDiscoveryService(options)
}

// Types
export type {
  ModuleMetadata,
  ModuleRoute,
  NavigationItem,
  NavigationBadge,
  RouteGuard,
  ModuleId,
  ModuleName,
  ModuleVersion,
  ModuleCandidate,
  PackageInfo,
  ModuleRegistration,
  RegistryEvent,
  ValidationError,
  ValidationResult,
  CacheEntry
} from './types'

export {
  ModuleCategory,
  ModuleStatus,
  RouteGuardType,
  NavigationBadgeType,
  ModuleMetadataSchema,
  ModuleRouteSchema,
  NavigationItemSchema,
  NavigationBadgeSchema,
  RouteGuardSchema,
  validateModuleMetadata
} from './types'

// Utilities
export {
  ModuleCache,
  BrowserEventEmitter,
  defaultEventEmitter,
  getPath,
  getFs,
  getGlob,
  initializePlatformModules,
  preloadBrowserModules,
  getPlatformInfo,
  createPlatformError,
  clearModuleCache,
  detectEnvironment,
  loadConditionalModule,
  loadConditionalModuleSync,
  getPathSeparator,
  getCurrentDirectory,
  isDevelopment,
  isProduction,
  isTest,
  isBrowser,
  isNode,
  getEnvironmentName,
  getPlatformName,
  createLogger,
  hasFeature,
  assertFeature,
  warnMissingFeature,
  ENV,
  FEATURES,

  loadModuleAsync,
  normalizeModuleId,
  isValidModuleId,
  resolveModulePath,
  getModulePackageName,
  hasPermission,
  hasRole,
  filterModulesByPermissions,
  filterModulesByRoles,
  sortModulesByPriority,
  sortModulesByName,
  groupModulesByCategory,
  validateModuleId,
  resolveDependencies,
  ModuleRegistryError,
  ModuleValidationError,
  ModuleDiscoveryError,
  measurePerformance,
  debounce,
  throttle
} from './utils'

export type {
  CacheOptions,
  CacheStats,
  EventListener,
  EventEmitterInterface,
  PlatformPath,
  PlatformFs,
  PlatformGlob,
  EnvironmentInfo,
  BrowserPath,
  BrowserFileSystem,
  BrowserGlob
} from './utils'

// Version
export const VERSION = '1.0.0'

// Default configuration
export const DEFAULT_REGISTRY_OPTIONS = {
  enableCaching: true,
  cacheTimeout: 5 * 60 * 1000, // 5 minutes
  validateOnRegister: true,
  allowDuplicates: false,
  autoInitialize: true
}

export const DEFAULT_DISCOVERY_OPTIONS = {
  packageScanPaths: ['packages/modules/*', 'packages/core/*'],
  configScanPaths: ['packages/modules/*', 'src/pages/modules/*'],
  packageNamePattern: '@dbs-portal/module-*',
  configFilePattern: 'module.config.{ts,js}',
  enablePackageScanning: true,
  enableConfigDetection: true,
  enableRuntimeRegistration: true,
  validateModules: true,
  ignorePatterns: ['node_modules/**', 'dist/**', '**/*.test.*', '**/*.spec.*']
}
